import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { peopleGateway } from '../../people';
import type { CompanyWorkLocationOption } from '../../people/types';
import { healthcareErrorMessage, isForbidden } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { todayIso } from '../rateDisplay';
import {
  REIMBURSEMENT_SEPARATE_NOTE, SYNTHETIC_RATES_NOTICE, SYNTHETIC_RATES_NOTICE_TITLE,
  type CreateServiceRateInput, type Discipline, type DisciplineAssignment, type ServiceRate, type ServiceType,
} from '../types';
import { useCompanyIndustry } from '../useCompanyIndustry';
import { useHealthcareCapabilities } from '../useHealthcareCapabilities';
import { AddDisciplineDialog } from './AddDisciplineDialog';
import { AllowedWorkTypesSection } from './AllowedWorkTypesSection';
import { DisciplineField } from './DisciplineCombobox';
import { EndDateForm } from './EndDateForm';
import { RatesTable } from './RatesTable';
import { ServiceRateForm } from './ServiceRateForm';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface DisciplineAndRatesPanelProps {
  employeeId: string;
  /** Called after any change, so the profile can re-read what depends on it. */
  onChanged?: (() => void) | undefined;
  /** Changes when the employee's primary discipline was changed elsewhere on the page, so the list is read again. */
  refreshKey?: string | null | undefined;
}

const isCurrent = (a: DisciplineAssignment, today: string) => a.effectiveTo === null || a.effectiveTo > today;

/**
 * "Discipline and service rates" on the employee's Payroll Details: the disciplines the employee holds now and held before (add another, end one),
 * and the employee's own service rates (list, add, end) for people who may see and change them. A discipline is the worker's professional
 * classification; a service rate is what the company pays for one kind of work. History is never edited: ending a row and adding a new one is
 * the only way to change it. The server decides every permission, and what is hidden here is only what the capability reads say is not offered.
 */
export function DisciplineAndRatesPanel({ employeeId, onChanged, refreshKey }: DisciplineAndRatesPanelProps) {
  const uid = useId();
  const caps = useHealthcareCapabilities();
  const { defaultTags, label: industryLabel, isHealthcare } = useCompanyIndustry();
  const synthetic = healthcareRatesGateway.isSynthetic;
  const today = todayIso();

  const [assignments, setAssignments] = useState<DisciplineAssignment[] | null>(null);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [rates, setRates] = useState<ServiceRate[] | null>(null);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [ratesForbidden, setRatesForbidden] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [serviceTypesReady, setServiceTypesReady] = useState(false);
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);
  const [nonce, setNonce] = useState(0);
  const [status, setStatus] = useState<string | null>(null);

  const [addingAssignment, setAddingAssignment] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const [pickedName, setPickedName] = useState('');
  const [makePrimary, setMakePrimary] = useState(false);
  const [from, setFrom] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [addDisciplineOpen, setAddDisciplineOpen] = useState(false);
  const [addDisciplineTyped, setAddDisciplineTyped] = useState('');
  const [endingId, setEndingId] = useState<string | null>(null);
  const [addingRate, setAddingRate] = useState(false);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .listDisciplineAssignments(employeeId, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) { setAssignments(rows); setAssignmentsError(null); } })
      .catch((err: unknown) => { if (!controller.signal.aborted) setAssignmentsError(healthcareErrorMessage(err, 'Could not load the disciplines.')); });
    healthcareRatesGateway
      .listEmployeeServiceRates(employeeId, { includeEnded: true }, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) { setRates(rows); setRatesError(null); setRatesForbidden(false); } })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRates(null);
        setRatesForbidden(isForbidden(err));
        setRatesError(isForbidden(err) ? null : healthcareErrorMessage(err, 'Could not load the service rates.'));
      });
    return () => controller.abort();
  }, [employeeId, nonce, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .listServiceTypes(undefined, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) { setServiceTypes(rows); setServiceTypesReady(true); } })
      .catch(() => undefined);
    peopleGateway.listCompanyWorkLocations().then((rows) => { if (!controller.signal.aborted) setLocations(rows.filter((l) => l.status === 'Active')); }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  const current = useMemo(() => (assignments ?? []).filter((a) => isCurrent(a, today)), [assignments, today]);
  const hasPrimary = current.some((a) => a.isPrimary);
  const assignedDisciplines = useMemo(
    () => [...new Map(current.map((a) => [a.disciplineId, { id: a.disciplineId, name: a.disciplineName }])).values()],
    [current],
  );
  const locationNames = useMemo(() => new Map(locations.map((l) => [l.id, l.name])), [locations]);
  const canAssign = caps.discipline?.canAssignDisciplines !== false;
  const canManageDisciplines = caps.discipline?.canManageDisciplines !== false;
  const canManageRates = caps.employeeRate?.canManageEmployeeRates !== false;

  function openAssignmentForm() {
    setPickedId(''); setPickedName(''); setReason(''); setFrom(todayIso()); setAssignError(null); setPickError(null);
    setMakePrimary(!hasPrimary);
    setAddingAssignment(true);
    setStatus(null);
  }

  async function saveAssignment(e: FormEvent) {
    e.preventDefault();
    if (!pickedId) {
      setPickError('Choose a worker category.');
      return;
    }
    setPickError(null);
    setAssignError(null);
    setAssignBusy(true);
    try {
      await healthcareRatesGateway.createDisciplineAssignment(employeeId, {
        disciplineId: pickedId, isPrimary: makePrimary, ...(from ? { effectiveFromDate: from } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setAddingAssignment(false);
      setStatus(`${pickedName} was added${makePrimary ? ' as the primary worker category' : ''}.`);
      reload();
      onChanged?.();
    } catch (err) {
      setAssignError(healthcareErrorMessage(err, 'Could not add the worker category.'));
    } finally {
      setAssignBusy(false);
    }
  }

  async function endAssignment(a: DisciplineAssignment, date: string) {
    await healthcareRatesGateway.endDisciplineAssignment(employeeId, a.id, date);
    setEndingId(null);
    setStatus(`${a.disciplineName} was ended.`);
    reload();
    onChanged?.();
  }

  async function createRate(input: CreateServiceRateInput) {
    await healthcareRatesGateway.createEmployeeServiceRate(employeeId, input);
    setAddingRate(false);
    setStatus('Service rate saved.');
    reload();
    onChanged?.();
  }

  async function endRate(rate: ServiceRate, date: string) {
    await healthcareRatesGateway.endEmployeeServiceRate(employeeId, rate.id, date, rate.concurrencyVersion);
    setStatus(date <= rate.effectiveFrom ? 'Service rate voided: it never applied.' : 'Service rate ended.');
    reload();
    onChanged?.();
  }

  const ordered = useMemo(() => [...(assignments ?? [])], [assignments]);
  const name = (a: DisciplineAssignment) => a.disciplineName || a.disciplineCode;

  return (
    <section className="hdc-panel" aria-labelledby={`${uid}-title`} data-testid="discipline-rates-panel">
      <h4 id={`${uid}-title`}>Worker category and rates</h4>
      <p className="hdc-note">
        A worker category is the kind of worker (called a discipline in many healthcare organizations). A rate is what your company pays for one kind of work, for example a service call or a
        Start of Care visit.
        {isHealthcare ? ` ${REIMBURSEMENT_SEPARATE_NOTE}` : ''}
      </p>
      {synthetic && (
        <p className="hdc-synthetic" role="note" data-testid="synthetic-notice"><strong>{SYNTHETIC_RATES_NOTICE_TITLE}</strong>{SYNTHETIC_RATES_NOTICE}</p>
      )}
      {status && <p className="jr-ok" role="status">{status}</p>}

      <h5>Worker categories</h5>
      {assignmentsError ? (
        <div role="alert">
          <p className="jr-error">{assignmentsError}</p>
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={reload}>Retry</button>
        </div>
      ) : assignments === null ? (
        <p className="muted" role="status">Loading worker categories…</p>
      ) : (
        <div className="jr-table-scroll" role="region" aria-label="Worker category assignments (scrolls sideways on a narrow screen)" tabIndex={0}>
          <table className="jr-table" aria-label="Worker category assignments">
            <thead>
              <tr>
                <th scope="col">Worker category</th><th scope="col">Kind</th><th scope="col">Effective from</th><th scope="col">Ends before</th><th scope="col">Status</th>
                {canAssign && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {ordered.length === 0 && (
                <tr><td colSpan={canAssign ? 6 : 5} className="muted">No worker category is set for this employee yet. Nothing is assumed from the role or the pay type.</td></tr>
              )}
              {ordered.map((a) => (
                <tr key={a.id}>
                  <th scope="row">{name(a)} <span className="muted">{a.disciplineCode}</span></th>
                  <td>{a.isPrimary ? 'Primary' : 'Additional'}</td>
                  <td>{a.effectiveFrom}</td>
                  <td>{a.effectiveTo ?? 'No end date'}</td>
                  <td><span className={`hdc-badge${isCurrent(a, today) ? ' hdc-badge--ok' : ''}`}>{isCurrent(a, today) ? 'Current' : 'Ended'}</span></td>
                  {canAssign && (
                    <td>
                      {a.effectiveTo === null && endingId !== a.id && (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`End ${name(a)}`} onClick={() => { setEndingId(a.id); setStatus(null); }}>End</button>
                      )}
                      {endingId === a.id && (
                        <EndDateForm label={`End ${name(a)}`} startDate={a.effectiveFrom} confirmLabel="Confirm end" onCancel={() => setEndingId(null)} onConfirm={(date) => endAssignment(a, date)} />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!canAssign && <p className="jr-readonly" role="note">You can see this employee&apos;s disciplines but not change them.</p>}
      {canAssign && !addingAssignment && assignments !== null && (
        <p><button type="button" className="jr-btn jr-btn--secondary" onClick={openAssignmentForm}>Add another worker category</button></p>
      )}
      {addingAssignment && (
        <form className="hdc-form" aria-label="Add a worker category for this employee" noValidate onSubmit={(e) => void saveAssignment(e)}>
          <div className="jr-grid">
            <div className="jr-field">
              <label htmlFor={`${uid}-pick`}>Worker category <span aria-hidden="true" className="jr-req">*</span></label>
              <DisciplineField
                a11y={{ id: `${uid}-pick`, 'data-field': 'discipline', 'aria-describedby': pickError ? `${uid}-pick-error` : undefined, 'aria-invalid': pickError ? true : undefined, 'aria-required': true }}
                value={pickedId}
                valueLabel={pickedName}
                canAdd={canManageDisciplines}
                excludeIds={current.map((a) => a.disciplineId)}
                onSelect={(d: Discipline) => { setPickedId(d.id); setPickedName(d.name); setPickError(null); }}
                onClear={() => { setPickedId(''); setPickedName(''); }}
                onAddRequested={(typed) => { setAddDisciplineTyped(typed); setAddDisciplineOpen(true); }}
              />
              {pickError && <p id={`${uid}-pick-error`} className="jr-error" role="alert">{pickError}</p>}
            </div>
            <div className="jr-field">
              <label htmlFor={`${uid}-from`}>Effective from</label>
              <input id={`${uid}-from`} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="jr-field jr-grid__wide">
              <label className="hdc-check">
                <input type="checkbox" checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} />
                <span>Make this the primary worker category</span>
              </label>
              <p className="jr-hint">{makePrimary && hasPrimary ? 'The current primary worker category will end on the date above.' : 'An employee can hold more than one worker category; one is the primary.'}</p>
            </div>
            <div className="jr-field jr-grid__wide">
              <label htmlFor={`${uid}-reason`}>Reason (optional)</label>
              <input id={`${uid}-reason`} value={reason} maxLength={300} autoComplete="off" onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>
          {assignError && <p className="jr-error jr-error--block" role="alert">{assignError}</p>}
          <div className="jr-actions hdc-form__actions">
            <button type="submit" className="jr-btn jr-btn--primary" disabled={assignBusy}>{assignBusy ? 'Saving…' : 'Save worker category'}</button>
            <button type="button" className="jr-btn jr-btn--secondary" disabled={assignBusy} onClick={() => setAddingAssignment(false)}>Cancel</button>
          </div>
        </form>
      )}

      <AllowedWorkTypesSection
        employeeId={employeeId}
        serviceTypes={serviceTypes}
        serviceTypesReady={serviceTypesReady}
        canEdit={canAssign}
        onChanged={onChanged}
      />

      <h5>Service rates for this employee</h5>
      <p className="hdc-note">
        Use these only when this person is paid differently from your company schedule. A company rate applies to everyone with the discipline; this
        employee&apos;s own rate wins over it for the same work.
      </p>
      {ratesForbidden && <p className="jr-readonly" role="note">You do not have permission to see this employee&apos;s service rates.</p>}
      {ratesError && (
        <div role="alert"><p className="jr-error">{ratesError}</p><button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={reload}>Retry</button></div>
      )}
      {!ratesForbidden && !ratesError && rates === null && <p className="muted" role="status">Loading service rates…</p>}
      {rates !== null && (
        <RatesTable
          label="Service rates for this employee"
          rows={rates}
          locationNames={locationNames}
          onEnd={canManageRates ? endRate : undefined}
          emptyMessage="No employee-specific service rates. The company schedule applies."
          markExamples
        />
      )}
      {rates !== null && !canManageRates && <p className="jr-readonly" role="note">You can see these rates but not change them.</p>}
      {rates !== null && canManageRates && !addingRate && (
        <p>
          <button type="button" className="jr-btn jr-btn--secondary" disabled={assignedDisciplines.length === 0} aria-describedby={assignedDisciplines.length === 0 ? `${uid}-norate` : undefined} onClick={() => { setAddingRate(true); setStatus(null); }}>
            Add a service rate
          </button>
          {assignedDisciplines.length === 0 && <span id={`${uid}-norate`} className="hdc-note"> Add a discipline first: a rate is for one of this employee&apos;s disciplines.</span>}
        </p>
      )}
      {addingRate && (
        <ServiceRateForm
          title="Add a service rate for this employee"
          disciplines={assignedDisciplines}
          serviceTypes={serviceTypes.filter((t) => t.isActive)}
          locations={locations}
          existing={rates ?? []}
          submitLabel="Save service rate"
          defaultDisciplineId={assignedDisciplines.length === 1 ? assignedDisciplines[0]!.id : undefined}
          onSubmit={createRate}
          onCancel={() => setAddingRate(false)}
        />
      )}

      {addDisciplineOpen && (
        <AddDisciplineDialog
          initialName={addDisciplineTyped}
          defaultTags={defaultTags}
          industryLabel={industryLabel}
          healthcare={isHealthcare}
          onClose={() => setAddDisciplineOpen(false)}
          onAdded={(d) => { setPickedId(d.id); setPickedName(d.name); setPickError(null); setAddDisciplineOpen(false); }}
        />
      )}
    </section>
  );
}
